<template>
  <div class="pets-plans">
    <!-- Columna izquierda: mascotas del tutor y apadrinadas. -->
    <v-card class="seccion-card pets-list" data-test="pets-list">
      <div v-if="showPets" class="list-head">
        <span class="list-title" data-test="pets-title">Mascotas del tutor · {{ petInfo.length }}</span>
        <v-btn
          v-if="can('users:add-pet')"
          small
          text
          color="primary"
          data-test="add-pet"
          @click="$emit('add-pet')"
        >
          <v-icon small left>mdi-plus</v-icon>Agregar
        </v-btn>
      </div>

      <div class="list-body" :class="{ 'is-scrollable': isScrollable }" data-test="list-body">
        <template v-if="!showPets" />
        <SourceError360 v-else-if="hasError('pets')" label="las mascotas" @retry="$emit('retry', 'pets')" />
        <v-skeleton-loader v-else-if="isLoading('pets')" type="list-item-two-line@3" data-test="pets-loading" />
        <div v-else-if="!petInfo.length" class="empty-state" data-test="no-pets">
          <p>El tutor no tiene mascotas registradas.</p>
          <v-btn
            v-if="can('users:add-pet')"
            small
            depressed
            color="primary"
            data-test="add-pet-empty"
            @click="$emit('add-pet')"
          >
            <v-icon small left>mdi-plus</v-icon>Agregar
          </v-btn>
        </div>
        <ul v-else class="item-list" aria-label="Mascotas del tutor">
          <li v-for="pet in petInfo" :key="pet.id">
            <button
              type="button"
              class="list-item"
              :class="{ 'is-selected': isSelected('pet', pet.id) }"
              :aria-pressed="String(isSelected('pet', pet.id))"
              :data-test="`pet-item-${pet.id}`"
              @click="select('pet', pet.id)"
            >
              <PetNameBadge :petName="pet.mascota" :species="pet.species" small showIcon />
              <span class="item-meta">
                <template v-if="currentMembership(pet)">
                  <span class="item-plan">{{ currentMembership(pet).planName }}</span>
                  <MembershipStatusChip outlined :status="statusOf(currentMembership(pet))" />
                </template>
                <span v-else class="item-plan is-muted">Sin plan vigente</span>
              </span>
            </button>
          </li>
        </ul>

        <template v-if="hasError('sponsorship')">
          <div class="list-subhead">Apadrinadas</div>
          <SourceError360 label="los apadrinamientos" @retry="$emit('retry', 'sponsorship')" />
        </template>
        <template v-else-if="sponsorship.length">
          <div class="list-subhead" data-test="sponsorship-title">Apadrinadas · {{ sponsorship.length }}</div>
          <ul class="item-list" aria-label="Mascotas apadrinadas">
            <li v-for="sponsor in sponsorship" :key="sponsor.id">
              <button
                type="button"
                class="list-item"
                :class="{ 'is-selected': isSelected('sponsorship', sponsor.id) }"
                :aria-pressed="String(isSelected('sponsorship', sponsor.id))"
                :data-test="`sponsorship-item-${sponsor.id}`"
                @click="select('sponsorship', sponsor.id)"
              >
                <PetNameBadge :petName="sponsor.name || '-'" :species="sponsor.species" small showIcon />
                <span class="item-meta">
                  <span class="item-plan">{{ sponsor.plansName }}</span>
                  <MembershipStatusChip outlined :status="statusOf(sponsor)" />
                </span>
              </button>
            </li>
          </ul>
        </template>
        <div
          v-else-if="!showPets && !isLoading('sponsorship')"
          class="empty-state"
          data-test="no-sponsorship"
        >
          <p>El tutor no tiene apadrinamientos.</p>
        </div>
      </div>
    </v-card>

    <!-- Columna derecha: detalle de lo seleccionado. -->
    <div class="pet-detail">
      <template v-if="selectedPet">
        <v-card class="seccion-card" data-test="pet-card">
          <v-card-title class="section-head detail-head">
            <PetNameBadge :petName="selectedPet.mascota" :species="selectedPet.species" :small="false" showIcon />
            <div class="detail-actions">
              <v-btn
                v-if="canAct('pets:update')"
                small
                outlined
                color="primary"
                data-test="edit-pet"
                @click="$emit('edit-pet', selectedPet)"
              >
                <v-icon small left>mdi-pencil</v-icon>Editar mascota
              </v-btn>
              <v-btn
                v-if="can('users:delete-pet')"
                small
                outlined
                color="error"
                :disabled="petHasMembership(selectedPet)"
                data-test="delete-pet"
                @click="$emit('delete-pet', selectedPet)"
              >
                <v-icon small left>mdi-delete</v-icon>Eliminar
              </v-btn>
            </div>
          </v-card-title>
          <p v-if="can('users:delete-pet') && petHasMembership(selectedPet)" class="delete-reason" data-test="delete-disabled-reason">
            No se puede eliminar: tiene una membresía asociada
          </p>
          <dl class="facts">
            <div class="fact"><dt>Especie</dt><dd>{{ speciesLabel(selectedPet.species) }}</dd></div>
            <div class="fact"><dt>Sexo</dt><dd>{{ selectedPet.sex || '-' }}</dd></div>
            <div class="fact"><dt>Edad</dt><dd data-test="pet-age">{{ ageText(selectedPet) }}</dd></div>
            <div class="fact"><dt>Raza</dt><dd>{{ selectedPet.raza || '-' }}</dd></div>
          </dl>
        </v-card>

        <v-card class="seccion-card" data-test="pet-memberships">
          <v-card-title class="section-head">
            <v-icon small>mdi-badge-account-horizontal-outline</v-icon>Membresías
          </v-card-title>

          <SourceError360 v-if="hasError('memberships')" label="las membresías" @retry="$emit('retry', 'memberships')" />
          <v-skeleton-loader v-else-if="isLoading('memberships')" type="table-row@2" data-test="memberships-loading" />
          <template v-else>
            <v-alert
              v-for="m in failedMemberships"
              :key="`failed-${m.id}`"
              type="error"
              text
              dense
              class="mx-4 mt-3 mb-0"
              :data-test="`payment-failed-${m.id}`"
            >
              <span>{{ paymentFailedText(m) }} · {{ m.planName }}</span>
              <template v-slot:append>
                <v-btn
                  v-if="can('users:regenerate-payment-link')"
                  small
                  text
                  color="error"
                  :data-test="`payment-failed-regenerate-${m.id}`"
                  @click="$emit('regenerate-payment-link', m)"
                >Regenerar link de pago</v-btn>
              </template>
            </v-alert>

            <div class="table-scroll">
              <v-data-table
                :headers="membershipHeaders"
                :items="selectedPetMemberships"
                item-key="id"
                disable-pagination
                hide-default-footer
                no-data-text="La mascota no tiene membresías"
                class="pdf-friendly-table"
              >
                <template v-slot:item.planName="{ item }">
                  <div class="cell-stack">
                    <span class="cell-title">
                      {{ item.planName }}
                      <CopyButton
                        :text="membershipCopyText(item)"
                        label="Copiar datos de la membresía"
                        :data-test="`copy-membership-${item.id}`"
                        @error="$emit('copy-error')"
                      />
                    </span>
                    <span class="cell-sub pw-num" :data-test="`membership-ref-${item.id}`">Ref {{ item.ref || '—' }}</span>
                  </div>
                </template>
                <template v-slot:item.status="{ item }">
                  <MembershipStatusChip outlined :status="statusOf(item)" />
                </template>
                <!-- Inicio, expiración y (si existe) desactivación, cada una con su rótulo. -->
                <template v-slot:item.validity="{ item }">
                  <dl class="dates" :data-test="`membership-dates-${item.id}`">
                    <div><dt>Inicio</dt><dd class="pw-num">{{ dateOrDash(item.startdate) }}</dd></div>
                    <div><dt>Expira</dt><dd class="pw-num">{{ dateOrDash(item.expirationDate) }}</dd></div>
                    <div v-if="hasDate(item.endDate)" class="is-deactivated">
                      <dt>Desactivada</dt><dd class="pw-num">{{ item.endDate }}</dd>
                    </div>
                  </dl>
                </template>
                <template v-slot:item.companyName="{ item }">
                  <div class="cell-stack">
                    <span v-if="item.companyName">
                      {{ item.companyName }}<template v-if="item.contratoCodigo"> — {{ item.contratoCodigo }}</template>
                    </span>
                    <span v-else class="grey--text">B2C</span>
                    <span class="cell-sub" :data-test="`membership-channel-${item.id}`">
                      Canal: {{ hasDate(item.acquisitionChannel) ? item.acquisitionChannel : '—' }}
                    </span>
                  </div>
                </template>
                <template v-slot:item.paymentInterval="{ item }">
                  {{ item.paymentInterval || '-' }}
                </template>
                <template v-slot:item.actions="{ item }">
                  <UserActions
                    :show-actions="actionsFor(item)"
                    :data-test="`membership-actions-${item.id}`"
                    @seeContract="$emit('see-contract', item)"
                    @updateContract="$emit('update-contract', item)"
                    @updateMembership="$emit('update-membership', item)"
                    @renewMembership="$emit('renew-membership', item)"
                    @regeneratePaymentLink="$emit('regenerate-payment-link', item)"
                  />
                </template>
              </v-data-table>
            </div>

            <div v-if="currentMembership(selectedPet)" class="current-usage" data-test="current-usage">
              <div>
                <span class="usage-label">Uso del plan vigente</span>
                <span class="usage-plan">{{ currentMembership(selectedPet).planName }}</span>
                <span class="usage-validity">{{ validityText(currentMembership(selectedPet)) }}</span>
              </div>
              <v-btn
                small
                text
                color="primary"
                data-test="view-usage"
                @click="$emit('view-usage', currentMembership(selectedPet).id)"
              >
                Ver uso en Coberturas y servicios<v-icon small right>mdi-arrow-right</v-icon>
              </v-btn>
            </div>
          </template>
        </v-card>

        <!-- Pagos de la membresía elegida (por defecto la vigente). Solo cobros exitosos: los
             rechazados no se registran (ver aviso de pago fallido arriba). -->
        <v-card
          v-if="canViewPayments && selectedPetMemberships.length"
          class="seccion-card"
          data-test="payments-section"
        >
          <v-card-title class="section-head">
            <v-icon small>mdi-cash-multiple</v-icon>Pagos
          </v-card-title>

          <!-- Con varias membresías se elige de cuál ver los pagos (o todas): a la vista, no en un menú. -->
          <div
            v-if="selectedPetMemberships.length > 1"
            class="payments-scope"
            role="group"
            aria-label="Membresía de los pagos"
            data-test="payments-membership-picker"
          >
            <button
              v-for="option in paymentsMembershipOptions"
              :key="option.id"
              type="button"
              class="scope-option"
              :class="{ 'is-selected': option.id === paymentsMembershipId }"
              :aria-pressed="String(option.id === paymentsMembershipId)"
              :data-test="`payments-scope-${option.id}`"
              @click="paymentsMembershipId = option.id"
            >
              <span class="scope-plan">{{ option.label }}</span>
              <MembershipStatusChip v-if="option.status !== null" outlined :status="option.status" />
              <span class="scope-count pw-num">{{ option.count }} {{ option.count === 1 ? 'pago' : 'pagos' }}</span>
            </button>
          </div>

          <SourceError360 v-if="payments.error" label="los pagos" @retry="fetchPayments" />
          <v-skeleton-loader v-else-if="payments.loading" type="table-row@3" data-test="payments-loading" />
          <template v-else>
            <dl class="payments-summary" data-test="payments-summary">
              <div class="payments-stat">
                <dt>Total recaudado</dt>
                <dd class="pw-num" data-test="payments-total">{{ totalCollected }}</dd>
              </div>
              <div v-if="showCalendar" class="payments-stat">
                <dt>{{ calendar.annual ? 'Año pagado' : 'Meses pagados' }}</dt>
                <dd class="pw-num" data-test="payments-paid-count">{{ paidCountText }}</dd>
              </div>
              <div class="payments-stat">
                <dt>Último pago</dt>
                <dd class="pw-num">
                  {{ lastPayment ? lastPayment.paymentDate : '—' }}
                  <span v-if="lastPayment" class="payments-stat-sub">· {{ lastPayment.value }}</span>
                </dd>
              </div>
              <div v-if="showCalendar && calendar.nextCharge" class="payments-stat">
                <dt>Próximo cobro (estimado)</dt>
                <dd class="pw-num" data-test="payments-next-charge">{{ calendar.nextCharge }}</dd>
              </div>
            </dl>

            <!-- Calendario de la vigencia: qué períodos están pagados y cuáles faltan. -->
            <div v-if="showCalendar" class="payments-calendar" data-test="payments-calendar">
              <ul class="months" :class="{ 'is-annual': calendar.annual }" aria-label="Períodos de la vigencia">
                <li
                  v-for="period in calendar.periods"
                  :key="period.key"
                  class="month"
                  :class="`is-${period.status}`"
                  :data-test="`period-${period.key}`"
                >
                  <b>{{ period.label }}</b>
                  <small class="pw-num">{{ periodText(period) }}</small>
                </li>
              </ul>
              <div class="legend">
                <span><i class="dot is-paid" />Pagado</span>
                <span><i class="dot is-missed" />Sin cobro (vencido)</span>
                <span><i class="dot is-next" />Próximo cobro</span>
                <span><i class="dot is-future" />Futuro</span>
              </div>
            </div>
            <p v-else-if="isUnchargedMembership" class="payments-note" data-test="payments-not-charged">
              Esta membresía no se cobra al tutor, por eso no tiene calendario de cobros.
            </p>

            <v-data-table
              :headers="paymentHeaders"
              :items="indexedPayments"
              item-key="rowKey"
              :items-per-page="5"
              :footer-props="{ 'items-per-page-options': [5], 'show-items-per-page': false }"
              :no-data-text="isAllMemberships ? 'La mascota no tiene pagos registrados' : 'Esta membresía no tiene pagos registrados'"
              class="pdf-friendly-table pagination-right payments-table"
              data-test="payments-table"
            />
          </template>
          <div class="payments-note">
            Se muestran los cobros exitosos. El mes cubierto y el próximo cobro son estimados según el orden de los pagos.
          </div>
        </v-card>
      </template>

      <v-card v-else-if="selectedSponsorship" class="seccion-card" data-test="sponsorship-detail">
        <v-card-title class="section-head">
          <v-icon small>mdi-heart-outline</v-icon>Apadrinamiento · {{ selectedSponsorship.name || '-' }}
        </v-card-title>
        <!-- Mismo formato que la tabla de Membresías. -->
        <div class="table-scroll">
          <v-data-table
            :headers="sponsorshipHeaders"
            :items="[selectedSponsorship]"
            item-key="id"
            disable-pagination
            hide-default-footer
            class="pdf-friendly-table"
          >
            <template v-slot:item.plansName="{ item }">
              <span class="cell-title">{{ item.plansName || '-' }}</span>
            </template>
            <template v-slot:item.status="{ item }">
              <MembershipStatusChip outlined :status="statusOf(item)" />
            </template>
            <template v-slot:item.validity="{ item }">
              <dl class="dates" :data-test="`sponsorship-dates-${item.id}`">
                <div><dt>Inicio</dt><dd class="pw-num">{{ dateOrDash(item.startDate) }}</dd></div>
                <div><dt>Próximo pago</dt><dd class="pw-num">{{ dateOrDash(item.nextPaymentDate) }}</dd></div>
                <div v-if="hasDate(item.endDate)" class="is-deactivated">
                  <dt>Desactivada</dt><dd class="pw-num">{{ item.endDate }}</dd>
                </div>
              </dl>
            </template>
            <template v-slot:item.organization="{ item }">
              {{ item.organization || '-' }}
            </template>
            <template v-slot:item.actions="{ item }">
              <UserActions
                :show-actions="sponsorshipActions(item)"
                :data-test="`sponsorship-actions-${item.id}`"
                @manageSponsorship="$emit('manage-sponsorship', item)"
                @changeSponsoredPet="$emit('change-sponsored-pet', item)"
              />
            </template>
          </v-data-table>
        </div>
      </v-card>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import Api360Service from '@/services/360.service.js';
import PetNameBadge from './PetNameBadge.vue';
import MembershipStatusChip from './MembershipStatusChip.vue';
import UserActions from './UserActions.vue';
import SourceError360 from './SourceError360.vue';
import CopyButton from './CopyButton.vue';
import { membershipCopyText } from '@/utils/vision360.utils';
import { representativeIds, membershipActions } from '@/utils/membership-actions.utils';
import { formatMoney } from '@/utils/service-usage.utils';
import { buildPaymentCalendar } from '@/utils/payment-calendar.utils';
import { can, canAct } from '@/utils/permissions';

const DEACTIVATED = 4;
const PAYMENT_FAILED = 3;
// Con más ítems la lista se desplaza sola para no estirar la columna.
const SCROLL_THRESHOLD = 10;
const SPECIES_LABELS = { dog: 'Perro', cat: 'Gato' };

const EMPTY_SOURCE = { loading: false, error: false };
const ALL_MEMBERSHIPS = 'all';

// Tag each row action needs (spec 132). `seeContract` only reads, so it needs none.
const ACTION_PERMISSIONS = {
  updateContract: ['memberships:update'],
  updateMembership: ['memberships:update', 'memberships:change-status'],
  renewMembership: ['users:renew-memberships'],
  regeneratePaymentLink: ['users:regenerate-payment-link'],
  manageSponsorship: ['shelter-pets:update'],
  changeSponsoredPet: ['shelter-pets:update']
};

function isActionAllowed(action) {
  const tags = ACTION_PERMISSIONS[action];
  return !tags || tags.some((tag) => canAct(tag));
}

// Pestaña «Mascotas y planes» de la 360 (113 US3): lista de mascotas (y apadrinadas) a la
// izquierda, y a la derecha la ficha, las membresías con sus acciones y el uso del plan vigente.
export default {
  name: 'PetsPlans360Tab',
  components: { PetNameBadge, MembershipStatusChip, UserActions, SourceError360, CopyButton },
  props: {
    // Mascotas del tutor con la forma de dataMixin.fetchPetData.
    petInfo: { type: Array, default: () => [] },
    // Membresías del tutor con la forma de dataMixin.fetchMembershipData.
    detailsPlan: { type: Array, default: () => [] },
    sponsorship: { type: Array, default: () => [] },
    // Sin `users:view-memberships` la pestaña muestra solo los apadrinamientos.
    showPets: { type: Boolean, default: true },
    // Ids de mascotas con alguna membresía: esas no se pueden eliminar.
    petsWithMembership: { type: Array, default: () => [] },
    token: { type: String, required: true },
    // Id (pgId) del tutor, para pedir su historial de pagos.
    userId: { type: String, default: '' },
    // Estado de carga de cada fuente: { pets, memberships, sponsorship } con { loading, error }.
    loadState: { type: Object, default: () => ({}) }
  },
  emits: [
    'add-pet', 'edit-pet', 'delete-pet', 'see-contract', 'update-contract', 'update-membership',
    'renew-membership', 'regenerate-payment-link', 'view-usage', 'retry', 'copy-error',
    'change-sponsored-pet', 'manage-sponsorship'
  ],
  data() {
    return {
      selection: null,
      // membershipId → fecha DD/MM/AA en que pasó a «Pago fallido» (null si el historial no la tiene).
      failedSince: {},
      // Historial de pagos del tutor (todas sus membresías), pedido una sola vez y filtrado aquí.
      payments: { loaded: false, loading: false, error: false, items: [] },
      paymentsMembershipId: null,
      membershipHeaders: [
        { text: 'Plan', value: 'planName', sortable: false },
        { text: 'Estado', value: 'status', sortable: false },
        { text: 'Fechas', value: 'validity', sortable: false },
        { text: 'Empresa / Contrato · Canal', value: 'companyName', sortable: false },
        { text: 'Intervalo', value: 'paymentInterval', sortable: false },
        { text: 'Acciones', value: 'actions', sortable: false }
      ],
      sponsorshipHeaders: [
        { text: 'Plan', value: 'plansName', sortable: false },
        { text: 'Estado', value: 'status', sortable: false },
        { text: 'Fechas', value: 'validity', sortable: false },
        { text: 'Organización', value: 'organization', sortable: false },
        { text: 'Acciones', value: 'actions', sortable: false }
      ]
    };
  },
  computed: {
    canViewPayments() {
      return can('users:view-payments');
    },
    representatives() {
      return representativeIds(this.detailsPlan);
    },
    membershipsByPet() {
      const byPet = {};
      for (const m of this.detailsPlan) {
        if (m.petId == null) continue;
        (byPet[m.petId] = byPet[m.petId] || []).push(m);
      }
      return byPet;
    },
    isScrollable() {
      return this.petInfo.length + this.sponsorship.length > SCROLL_THRESHOLD;
    },
    selectedPet() {
      if (!this.selection || this.selection.type !== 'pet') return null;
      return this.petInfo.find((p) => p.id === this.selection.id) || null;
    },
    selectedSponsorship() {
      if (!this.selection || this.selection.type !== 'sponsorship') return null;
      return this.sponsorship.find((s) => s.id === this.selection.id) || null;
    },
    selectedPetMemberships() {
      return this.selectedPet ? this.membershipsByPet[this.selectedPet.id] || [] : [];
    },
    failedMemberships() {
      return this.selectedPetMemberships.filter((m) => this.statusOf(m) === PAYMENT_FAILED);
    },
    paymentsCountByMembership() {
      const counts = {};
      for (const p of this.payments.items) counts[p.membershipId] = (counts[p.membershipId] || 0) + 1;
      return counts;
    },
    // Una opción por membresía de la mascota y, al final, «Todas».
    paymentsMembershipOptions() {
      const options = this.selectedPetMemberships.map((m) => ({
        id: m.id,
        label: `${m.planName} · ${this.validityText(m)}`,
        status: this.statusOf(m),
        count: this.paymentsCountByMembership[m.id] || 0
      }));
      const total = options.reduce((sum, o) => sum + o.count, 0);
      return [...options, { id: ALL_MEMBERSHIPS, label: 'Todas', status: null, count: total }];
    },
    isAllMemberships() {
      return this.paymentsMembershipId === ALL_MEMBERSHIPS;
    },
    // El backend los entrega del más reciente al más antiguo.
    membershipPayments() {
      if (this.isAllMemberships) {
        const ids = new Set(this.selectedPetMemberships.map((m) => m.id));
        return this.payments.items.filter((p) => ids.has(p.membershipId));
      }
      return this.payments.items.filter((p) => p.membershipId === this.paymentsMembershipId);
    },
    paymentsMembership() {
      return this.selectedPetMemberships.find((m) => m.id === this.paymentsMembershipId) || null;
    },
    isUnchargedMembership() {
      return Boolean(this.paymentsMembership && this.paymentsMembership.isPaidPlan === false);
    },
    // Con «Todas» el mes cubierto no aplica (es por membresía) y se muestra de qué plan es cada pago.
    paymentHeaders() {
      const middle = this.isAllMemberships
        ? { text: 'Plan', value: 'planName', sortable: false }
        : { text: 'Mes cubierto', value: 'coveredPeriod', sortable: false };
      return [
        { text: '#', value: 'number', sortable: false, width: 56 },
        { text: 'Fecha de pago', value: 'paymentDate', sortable: false },
        middle,
        { text: 'Monto', value: 'value', sortable: false, align: 'end' }
      ];
    },
    // Las membresías que no paga el tutor (planes de empresa) no tienen calendario de cobros.
    showCalendar() {
      return Boolean(this.paymentsMembership && this.paymentsMembership.isPaidPlan !== false && this.calendar.periods.length);
    },
    calendar() {
      return buildPaymentCalendar(this.paymentsMembership, this.membershipPayments);
    },
    paidCountText() {
      return `${this.calendar.paidCount} de ${this.calendar.periods.length}`;
    },
    // Numeración descendente (el más reciente tiene el número más alto, como un correlativo de cobros).
    indexedPayments() {
      const total = this.membershipPayments.length;
      const covered = this.calendar.coveredByPayment;
      return this.membershipPayments.map((p, i) => ({
        ...p,
        number: total - i,
        coveredPeriod: covered.get(i) || '—',
        rowKey: `${p.membershipId}-${i}`
      }));
    },
    lastPayment() {
      return this.membershipPayments[0] || null;
    },
    // Suma de los cobros de la membresía con el monto numérico del backend (`amount`), en la
    // moneda de sus pagos; sin pagos, «—».
    totalCollected() {
      if (!this.membershipPayments.length) return '—';
      const total = this.membershipPayments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
      const currency = (this.membershipPayments.find((p) => p.currency) || {}).currency || 'CLP';
      return formatMoney(total, currency);
    }
  },
  watch: {
    petInfo: { immediate: true, handler: 'ensureSelection' },
    sponsorship: 'ensureSelection',
    detailsPlan: 'ensureSelection',
    failedMemberships: { immediate: true, handler: 'loadFailedSince' },
    // Al cambiar de mascota se muestran los pagos de su membresía vigente (o la primera).
    selectedPetMemberships: { immediate: true, handler: 'resetPaymentsMembership' }
  },
  methods: {
    hasError(source) {
      return Boolean((this.loadState[source] || EMPTY_SOURCE).error);
    },
    isLoading(source) {
      return Boolean((this.loadState[source] || EMPTY_SOURCE).loading);
    },
    statusOf(item) {
      return parseInt(item.status, 10);
    },
    // Plan vigente de la mascota: su membresía representativa, salvo que esté desactivada.
    currentMembership(pet) {
      const memberships = this.membershipsByPet[pet.id] || [];
      return memberships.find((m) => this.representatives.has(m.id) && this.statusOf(m) !== DEACTIVATED) || null;
    },
    petHasMembership(pet) {
      return this.petsWithMembership.includes(pet.id);
    },
    can,
    canAct,
    actionsFor(item) {
      return membershipActions(item, this.representatives).filter(isActionAllowed);
    },
    // «Cambiar mascota» (p.ej. la apadrinada fue adoptada) no aplica a un apadrinamiento desactivado.
    sponsorshipActions(item) {
      const actions =
        this.statusOf(item) === DEACTIVATED
          ? ['manageSponsorship']
          : ['manageSponsorship', 'changeSponsoredPet'];
      return actions.filter(isActionAllowed);
    },
    isSelected(type, id) {
      return Boolean(this.selection && this.selection.type === type && this.selection.id === id);
    },
    select(type, id) {
      this.selection = { type, id };
    },
    // Conserva la selección si sigue existiendo; si no, la primera mascota con plan vigente,
    // luego la primera mascota y, sin mascotas, la primera apadrinada.
    ensureSelection() {
      if (this.selectedPet || this.selectedSponsorship) return;
      const pet = this.petInfo.find((p) => this.currentMembership(p)) || this.petInfo[0];
      if (pet) {
        this.select('pet', pet.id);
      } else if (this.sponsorship.length) {
        this.select('sponsorship', this.sponsorship[0].id);
      } else {
        this.selection = null;
      }
    },
    // Pide el historial solo de las membresías con pago fallido visibles y que no se pidieron antes.
    loadFailedSince(memberships) {
      memberships
        .filter((m) => !Object.prototype.hasOwnProperty.call(this.failedSince, m.id))
        .forEach((m) => this.fetchFailedSince(m.id));
    },
    async fetchFailedSince(membershipId) {
      this.$set(this.failedSince, membershipId, null);
      try {
        const history = await Api360Service.getMembershipStatusHistory(membershipId, this.token);
        this.failedSince[membershipId] = this.latestFailedDate(Array.isArray(history) ? history : []);
      } catch (error) {
        // Sin historial el aviso se muestra igual, sin fecha (research R4).
        this.failedSince[membershipId] = null;
      }
    },
    latestFailedDate(history) {
      const latest = history
        .filter((row) => Number(row.newStatus) === PAYMENT_FAILED && row.createdAt)
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
      return latest ? moment(latest.createdAt).format('DD/MM/YY') : null;
    },
    resetPaymentsMembership(memberships) {
      if (!memberships.length) {
        this.paymentsMembershipId = null;
        return;
      }
      const current = this.selectedPet ? this.currentMembership(this.selectedPet) : null;
      this.paymentsMembershipId = (current || memberships[0]).id;
      if (!this.payments.loaded && !this.payments.loading) this.fetchPayments();
    },
    async fetchPayments() {
      if (!this.userId || !this.canViewPayments) return;
      this.payments = { ...this.payments, loading: true, error: false };
      try {
        const data = await Api360Service.getPaymentsHistory(this.userId, this.token);
        const items = (data && data.membershipPayments) || [];
        this.payments = { loaded: true, loading: false, error: false, items };
      } catch (error) {
        // La falla queda en la sección con «Reintentar»; el resto de la pestaña sigue igual.
        this.payments = { ...this.payments, loading: false, error: true };
      }
    },
    // Pagado: día de pago (el monto está en la tabla); próximo: su fecha estimada; vencido o futuro: sin dato.
    periodText(period) {
      if (period.status === 'paid') return period.payment.paymentDate.slice(0, 5);
      if (period.status === 'next') return period.date.slice(0, 5);
      if (period.status === 'missed') return 'Sin cobro';
      return '—';
    },
    paymentFailedText(m) {
      const since = this.failedSince[m.id];
      return since ? `Pago fallido desde ${since}` : 'Pago fallido';
    },
    speciesLabel(species) {
      return SPECIES_LABELS[species] || species || '-';
    },
    ageText(pet) {
      const birthdate = this.formatBirthdate(pet.birthdate);
      if (pet.edad && birthdate) return `${pet.edad} (${birthdate})`;
      return pet.edad || birthdate || '-';
    },
    formatBirthdate(value) {
      if (!value) return null;
      const date = moment.utc(value, [moment.ISO_8601, 'DD/MM/YYYY'], true);
      return date.isValid() ? date.format('DD/MM/YYYY') : null;
    },
    membershipCopyText,
    // Las fechas y el canal llegan como '-' cuando no existen (dataMixin.fetchMembershipData).
    hasDate(value) {
      return Boolean(value) && value !== '-';
    },
    dateOrDash(value) {
      return this.hasDate(value) ? value : '—';
    },
    validityText(m) {
      const start = m.startdate || '-';
      const end = m.expirationDate && m.expirationDate !== '-' ? m.expirationDate : null;
      return end ? `${start} – ${end}` : start;
    }
  }
};
</script>

<style scoped>
.pets-plans {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 20px;
}
.pets-list {
  flex: 1 1 280px;
  max-width: 380px;
}
.pet-detail {
  flex: 3 1 520px;
  min-width: 0;
}
/* Cuando la detalle baja a otra fila, la lista ocupa todo el ancho. */
@media (max-width: 900px) {
  .pets-list {
    max-width: none;
  }
}

.seccion-card {
  margin-bottom: 20px;
}
.section-head {
  font-size: 0.9375rem;
  font-weight: 600;
  padding: 15px 20px;
  border-bottom: 1px solid var(--pw-divider);
}
.section-head .v-icon {
  margin-right: 8px;
  color: var(--pw-text-muted);
}

.list-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px 10px 20px;
  border-bottom: 1px solid var(--pw-divider);
}
.list-title {
  font-size: 0.9375rem;
  font-weight: 600;
}
.list-subhead {
  padding: 14px 20px 6px;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.045em;
  color: var(--pw-text-faint);
  border-top: 1px solid var(--pw-divider);
}
.list-body.is-scrollable {
  max-height: 560px;
  overflow-y: auto;
}
.item-list {
  list-style: none;
  padding: 6px 0;
  margin: 0;
}
.list-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  width: 100%;
  padding: 10px 20px;
  text-align: left;
  border-left: 3px solid transparent;
}
.list-item:hover {
  background: var(--pw-divider);
}
.list-item:focus-visible {
  outline: 2px solid var(--v-primary-base);
  outline-offset: -2px;
}
.list-item.is-selected {
  border-left-color: var(--v-primary-base);
  background: var(--pw-info-tint);
}
.item-meta {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 0.8125rem;
}
.item-plan.is-muted {
  color: var(--pw-text-muted);
}

.detail-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
}
.detail-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.delete-reason {
  margin: 8px 20px 0;
  font-size: 0.78rem;
  color: var(--pw-text-muted);
}
.facts {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
  padding: 16px 20px;
  margin: 0;
}
.fact dt {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.045em;
  color: var(--pw-text-faint);
}
.fact dd {
  margin: 2px 0 0;
  font-weight: 500;
}

.table-scroll {
  overflow-x: auto;
}
.cell-stack {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 0;
}
.cell-title {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-weight: 500;
}
.cell-sub {
  font-size: 0.75rem;
  color: var(--pw-text-muted);
}
.dates {
  display: grid;
  grid-template-columns: auto auto;
  column-gap: 8px;
  row-gap: 2px;
  margin: 0;
  padding: 6px 0;
  font-size: 0.8125rem;
}
.dates > div {
  display: contents;
}
.dates dt {
  font-size: 0.72rem;
  color: var(--pw-text-muted);
}
.dates dd {
  margin: 0;
}
.dates .is-deactivated dt,
.dates .is-deactivated dd {
  color: var(--pw-error);
  font-weight: 600;
}
.pdf-friendly-table {
  border: 0;
}

.payments-scope {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 20px;
  border-bottom: 1px solid var(--pw-divider);
}
.scope-option {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  font-size: 0.8125rem;
  border: 1px solid var(--pw-divider);
  border-radius: 8px;
}
.scope-option:hover {
  background: var(--pw-divider);
}
.scope-option:focus-visible {
  outline: 2px solid var(--v-primary-base);
  outline-offset: 2px;
}
.scope-option.is-selected {
  border-color: var(--v-primary-base);
  background: var(--pw-info-tint);
}
.scope-plan {
  font-weight: 600;
}
.scope-count {
  color: var(--pw-text-muted);
}
.payments-summary {
  margin: 0;
  padding: 14px 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px 32px;
  border-bottom: 1px solid var(--pw-divider);
}
.payments-stat dt {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.045em;
  text-transform: uppercase;
  color: var(--pw-text-muted);
}
.payments-stat dd {
  margin: 2px 0 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--pw-text);
}
.payments-stat .payments-stat-sub {
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--pw-text-muted);
}

.payments-calendar {
  padding: 16px 20px 12px;
  border-bottom: 1px solid var(--pw-divider);
}
.months {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 8px;
}
.months.is-annual {
  grid-template-columns: minmax(0, 220px);
}
@media (max-width: 1100px) {
  .months:not(.is-annual) {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }
}
.month {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 10px 4px;
  text-align: center;
  border: 1px solid var(--pw-divider);
  border-radius: 8px;
}
.month b {
  font-size: 0.8125rem;
}
.month small {
  font-size: 0.72rem;
  color: var(--pw-text-faint);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.month.is-paid {
  background: var(--pw-success-tint);
  border-color: var(--pw-success);
}
.month.is-paid small {
  color: var(--pw-success);
}
.month.is-missed {
  background: var(--pw-error-tint);
  border-color: var(--pw-error);
}
.month.is-missed small {
  color: var(--pw-error);
}
.month.is-next {
  border-style: dashed;
  border-color: var(--v-primary-base);
}
.month.is-next small {
  color: var(--v-primary-base);
}
.month.is-future {
  opacity: 0.55;
}
.legend {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 10px;
  font-size: 0.75rem;
  color: var(--pw-text-muted);
}
.dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  margin-right: 6px;
  vertical-align: -1px;
  border: 1px solid var(--pw-divider);
  border-radius: 3px;
}
.dot.is-paid {
  background: var(--pw-success-tint);
  border-color: var(--pw-success);
}
.dot.is-missed {
  background: var(--pw-error-tint);
  border-color: var(--pw-error);
}
.dot.is-next {
  border-style: dashed;
  border-color: var(--v-primary-base);
}
.dot.is-future {
  opacity: 0.55;
}
.payments-note {
  padding: 8px 20px 12px;
  font-size: 0.78rem;
  color: var(--pw-text-muted);
}
.pagination-right::v-deep .v-data-footer {
  justify-content: flex-end;
}

.current-usage {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 20px;
  border-top: 1px solid var(--pw-divider);
}
.current-usage > div {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px;
}
.usage-label {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.045em;
  color: var(--pw-text-faint);
}
.usage-plan {
  font-weight: 600;
}
.usage-validity {
  font-size: 0.8125rem;
  color: var(--pw-text-muted);
}

.empty-state {
  padding: 24px 20px;
  color: var(--pw-text-muted);
}
</style>
